// The full column set, keyed by `name`. The table picks which of these to show
// via its `tableColumns` prop, so every consumer shares one definition.

import React from 'react';
import { t } from '@lingui/macro';
import { Button } from '@decisiv/ui-components';
import Newspaper from '@decisiv/iconix/lib/components/Newspaper';
import startCase from 'lodash/startCase';

import { formatTimestamp } from '../../utils/dates';

import StatusCell from './StatusCell';
import TraceIdCell from './TraceIdCell';

const EM_DASH = '⁠—';

const attrs = (data) => data?.rowData?.attributes ?? {};

export default function buildExecutionColumns({
  onSort,
  onShowDetails,
  retry,
  executions,
  maxRetryCount,
}) {
  return [
    {
      name: 'integration_name',
      title: t`INTEGRATION`,
      DataCell: (data) => (
        <a
          target="_blank"
          href={`/integrations/${attrs(data).integration_id}`}
          rel="noreferrer"
        >
          {attrs(data).integration_name}
        </a>
      ),
    },
    {
      name: 'x_decisiv_trace_id',
      title: 'X-DECISIV-TRACE-ID',
      DataCell: (data) => (
        <TraceIdCell traceId={attrs(data).x_decisiv_trace_id} />
      ),
    },
    {
      name: 'location_name',
      title: t`LOCATION`,
      sortBy: onSort('attributes.location_name'),
      DataCell: (data) => (
        <a
          target="_blank"
          href={`/location_settings/${attrs(data).configured_integration_id}`}
          rel="noreferrer"
        >
          {attrs(data).location_name}
        </a>
      ),
    },
    {
      name: 'city',
      title: 'CITY/STATE',
      sortBy: onSort('attributes.city'),
      DataCell: (data) =>
        attrs(data).city && attrs(data).state ? (
          <span>
            {attrs(data).city}, {attrs(data).state}
          </span>
        ) : (
          EM_DASH
        ),
    },
    {
      name: 'http_status',
      title: t`STATUS`,
      sortBy: onSort('attributes.status'),
      DataCell: (data) => (
        <StatusCell
          row={data.rowData}
          retry={retry}
          executions={executions}
          maxRetryCount={maxRetryCount}
        />
      ),
    },
    {
      name: 'source',
      title: t`SOURCE`,
      sortBy: onSort('attributes.source'),
      DataCell: (data) => {
        const { source } = attrs(data);
        if (!source) return EM_DASH;
        return (
          <span>{startCase(source.replace(/_/g, ' ').toLowerCase())}</span>
        );
      },
    },
    {
      name: 'trigger_type',
      title: t`Trigger`,
      sortBy: onSort('attributes.trigger_type'),
      DataCell: (data) => (
        <span>
          {attrs(data).trigger_type === 'http_api_request'
            ? t`Http`
            : t`Application Event`}
        </span>
      ),
    },
    {
      name: 'duration',
      title: t`DURATION`,
      sortBy: onSort('attributes.time_taken'),
      DataCell: (data) =>
        attrs(data).time_taken ? (
          <span>
            {attrs(data).time_taken}{' '}
            {attrs(data).time_taken > 1 ? t`seconds` : t`milliseconds`}
          </span>
        ) : (
          EM_DASH
        ),
    },
    {
      name: 'started',
      title: t`STARTED`,
      sortBy: onSort('attributes.start_date'),
      // A missing start_date used to reach moment(undefined), which formatted
      // as the current time — a timestamp the execution never had.
      DataCell: (data) => {
        const { status, start_date: startDate } = attrs(data);
        if (!status) return EM_DASH;

        const started = formatTimestamp(startDate);
        return started ? <span>{started}</span> : EM_DASH;
      },
    },
    {
      name: 'actions',
      DataCell: (data) => (
        <Button
          icon={Newspaper}
          aria-label={t`View execution details`}
          variant="ghost"
          onClick={() => onShowDetails(data)}
          id={`showLogModalBtn-${data.rowData.id}`}
          style={{ cursor: 'pointer' }}
        />
      ),
    },
  ];
}
